import { useState } from "react";
import { FileText, Home, Users, Key, Calendar, PoundSterling } from "lucide-react";
import GuaranteedRentClosedModal from "./GuaranteedRentClosedModal";

const steps = [
  { Icon: FileText, title: "Free valuation", description: "Share your property details and we'll provide a fixed monthly income offer within 24 hours, based on real serviced accommodation data for your area." },
  { Icon: Home, title: "Property visit", description: "We visit your property to assess its condition, discuss any improvements that could increase your offer, and answer your questions face to face." },
  { Icon: Key, title: "Agreement signed", description: "We sign a guaranteed rent agreement — typically 2–3 years. We become your tenant; you receive a fixed monthly income from day one." },
  { Icon: Users, title: "We set it up", description: "We furnish, photograph, and list the property across Airbnb and 16+ platforms as serviced accommodation. Subject to approval, we may carry out light refurbishments." },
  { Icon: Calendar, title: "We run everything", description: "Cleaning, maintenance, guest management, compliance — all handled by our team. You have no day-to-day involvement." },
  { Icon: PoundSterling, title: "You get paid", description: "Receive your guaranteed rent on the agreed date, every single month — whether the property is occupied or sitting empty." },
];

export default function GuaranteedRentHowItWorks() {
  const [showModal, setShowModal] = useState(false);

  return (
    <section style={{ backgroundColor: "#F4F1E7", padding: "clamp(60px,7vw,96px) clamp(20px,5vw,80px)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5C7268", marginBottom: 12, marginTop: 0 }}>
            How It Works
          </p>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em", lineHeight: 1.15, color: "#112215", margin: "0 auto 14px", maxWidth: 640 }}>
            Six steps to guaranteed income.
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: "#5C7268", lineHeight: 1.6, maxWidth: 520, margin: "0 auto" }}>
            From valuation to first payment — we handle everything. Most properties are live within 2–3 weeks.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }} className="gr-steps-grid">
          {steps.map(({ Icon, title, description }, i) => (
            <div
              key={title}
              onClick={() => setShowModal(true)}
              style={{
                background: "#fff",
                borderRadius: 14,
                padding: "28px 26px",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                transition: "transform 0.2s, box-shadow 0.2s",
                border: "1px solid #E8E4D5",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.08)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <div style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: "#F4F1E7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Icon size={20} color="#CD533B" strokeWidth={1.8} />
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 18, color: "#112215", letterSpacing: "-0.01em" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
              </div>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 16, color: "#112215", marginBottom: 10, marginTop: 0, letterSpacing: "-0.01em" }}>
                {title}
              </h3>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "#5C7268", lineHeight: 1.65, margin: 0 }}>
                {description}
              </p>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 48 }}>
          <button
            onClick={() => setShowModal(true)}
            style={{
              background: "#112215", color: "#fff", border: "none", borderRadius: 8,
              padding: "14px 32px", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600,
              cursor: "pointer", transition: "background 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#1A3A2A"}
            onMouseLeave={e => e.currentTarget.style.background = "#112215"}
          >
            Get your free valuation
          </button>
        </div>
      </div>

      {showModal && <GuaranteedRentClosedModal onClose={() => setShowModal(false)} />}

      <style>{`
        @media (max-width: 900px) { .gr-steps-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 560px) { .gr-steps-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}